import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { useAppSelector } from "../../helpers/hooks";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CardHeader from "@mui/material/CardHeader";
import Teams from "../../static/images/microsoft-teams.svg";
import Email from "../../static/images/email-icon.svg";
import EmailSelected from "../../static/images/email-icon-white.svg";
import TeamsSelected from "../../static/images/microsoft-teams-white.svg";
import Badge from "@mui/material/Badge";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";

interface UserInitialState {
  id: 0;
  shortId: "";
  userName: "";
  employeeId: "";
  manager: "";
  dateOfJoining: null;
  dateOfBirth: null;
  designation: null;
  userType: "";
  department: "";
  userEmail: "";
  imageName: "";
  lastSwiftUpdate: null;
}

interface ShareChannel {
  Email: boolean;
  Teams: boolean;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const ShareEvent = ({
  open,
  handleClose,
  eventData,
}: {
  open: boolean;
  handleClose: () => void;
  eventData: any;
}) => {
  const users = useAppSelector((state) => state.userProfile.users);
  const [selectedUser, setSelectedUser] = useState<UserInitialState>();
  const [selectedShareChannel, setSelectedShareChannel] =
    useState<ShareChannel>({
      Email: true,
      Teams: false,
    });
  const [filteredUsers, setFilteredUsers] = React.useState([]);
  const [userShortIds, setUserShortIds] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [enableShare, setEnableShare] = React.useState(false);

  useEffect(() => {
    const shortids: any = users.map((item: any, index: any) => item.shortId);
    setUserShortIds(shortids);
  }, [users]);

  const handleInputChange = (shortId: string) => {
    const filteredSuggestions =
      shortId.length > 0
        ? users?.filter((user: UserInitialState) => {
            return user?.shortId === shortId;
          })
        : [];
    setSelectedUser(filteredSuggestions[0]);
    setEnableShare(true);
  };

  const handleSearchInput = (e: any) => {
    setEnableShare(false);
    const value: string = e;
    setSearchValue(value);
    if (value) {
      const chars = value.toLowerCase().split("");
      const filteredSuggestions =
        value.length > 0
          ? userShortIds.filter((name: string) => {
              return chars.every((char) =>
                name?.replaceAll(" ", "").toLowerCase().includes(char)
              );
            })
          : [];
      setFilteredUsers(filteredSuggestions);
    } else {
      setFilteredUsers([]);
    }
  };

  function onShareClicked() {
    const recipientName = selectedUser?.userName || "";
    const recipientEmail = selectedUser?.userEmail || "";
    const subject = `Event - ${eventData.title || "Details"}`;
    const message = `Hey ${recipientName}, Have a look at this event: ${process.env.REACT_APP_EVENT_URL}${eventData.id}`;

    const teamsLink = `MSTeams:/l/chat/0/0?users=${encodeURIComponent(
      recipientEmail
    )}&message=${encodeURIComponent(message)}`;
    const mailLink = `mailto:${recipientEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(message)}`;
    if (recipientEmail) {
      window.location.href = selectedShareChannel.Teams ? teamsLink : mailLink;
    }
    handleClose();
  }

  function emailChannelClicked() {
    setSelectedShareChannel((prevState) => ({
      ...prevState,
      Email: true,
      Teams: false,
    }));
  }

  function teamsChannelClicked() {
    setSelectedShareChannel((prevState) => ({
      ...prevState,
      Email: false,
      Teams: true,
    }));
  }

  return (
    <>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="xs"
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
          className="modal-title"
        >
          <Typography sx={{ marginRight: 4 }}>{eventData.title}</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ backgroundColor: "#EAEBEB" }}>
          <Box component="form" noValidate>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl variant="standard" fullWidth>
                  <Autocomplete
                    value={searchValue}
                    inputValue={searchValue}
                    open={searchValue !== "" && !enableShare ? true : false}
                    disablePortal
                    noOptionsText={"No user found"}
                    id="freeSolo"
                    disableClearable
                    options={filteredUsers}
                    sx={{ width: "210%" }}
                    onInputChange={(e, v) => handleSearchInput(v)}
                    onChange={(e, value: string) => {
                      {
                        enableShare && setEnableShare(false);
                      }
                      handleInputChange(value);
                    }}
                    renderInput={(params) => (
                      <TextField label="Short Id" {...params} size="small" />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container sx={{ pt: 2, left: 2 }} spacing={9}>
              <Grid sx={{ pr: 2 }}>
                <Typography fontWeight={400}>Share by: </Typography>
                <CardHeader
                  action={
                    <Grid size={{ xs: 4 }} ml={1}>
                      <Badge
                        badgeContent={
                          selectedShareChannel?.Teams ? (
                            <CheckBoxOutlinedIcon
                              style={{ fontSize: 17, fill: "#000" }}
                            />
                          ) : null
                        }
                        sx={{ p: "0px 0px" }}
                      >
                        <IconButton
                          disableFocusRipple
                          disableRipple
                          aria-label="settings"
                          sx={{
                            background: selectedShareChannel?.Teams
                              ? "#000"
                              : "#fff",
                            boxShadow: 4,
                          }}
                          onClick={() => teamsChannelClicked()}
                        >
                          <Avatar
                            alt="Teams"
                            src={
                              selectedShareChannel?.Teams
                                ? TeamsSelected
                                : Teams
                            }
                            sx={{
                              m: 1,
                              borderRadius: 0,
                              width: "22px",
                              height: "22px",
                              color: "#0078D6",
                            }}
                          />
                        </IconButton>
                      </Badge>

                      <Typography
                        variant="h6"
                        sx={{ my: 1, ml: 1.5 }}
                        fontWeight={500}
                        fontSize={13}
                      >
                        Teams
                      </Typography>
                    </Grid>
                  }
                  className="card-edit-icon"
                />
                <CardHeader
                  action={
                    <Grid size={{ xs: 6 }} mr={1}>
                      <Badge
                        badgeContent={
                          selectedShareChannel?.Email ? (
                            <CheckBoxOutlinedIcon
                              style={{ fontSize: 17, fill: "#000" }}
                            />
                          ) : null
                        }
                        sx={{ p: "0px 0px" }}
                      >
                        <IconButton
                          disableFocusRipple
                          disableRipple
                          aria-label="settings"
                          sx={{
                            background: selectedShareChannel?.Email
                              ? "#000"
                              : "#fff",
                            boxShadow: 4,
                          }}
                          onClick={() => emailChannelClicked()}
                        >
                          <Avatar
                            alt="Teams"
                            src={
                              selectedShareChannel?.Email
                                ? EmailSelected
                                : Email
                            }
                            sx={{
                              m: 1,
                              borderRadius: 0,
                              width: "22px",
                              height: "22px",
                            }}
                          />
                        </IconButton>
                      </Badge>
                      <Typography
                        variant="h6"
                        sx={{ my: 1, ml: 1.5 }}
                        fontWeight={500}
                        fontSize={13}
                      >
                        Email
                      </Typography>
                    </Grid>
                  }
                  className="card-edit-icon"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ alignSelf: "center" }}>
          <Button
            disabled={!enableShare}
            variant="contained"
            sx={{ textTransform: "capitalize" }}
            style={{ borderRadius: 4 }}
            className="mui-modal-button"
            onClick={() => onShareClicked()}
          >
            Share
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

export default ShareEvent;
